import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useExamInsights } from '../context';

interface InsightsOpenBannerProps {
  /** Analyses left in the learner's running plan; the line is left out when the plan has no allowance to show. */
  analysesRemaining: number;
  analysisLimit: number;
  /** Where the full answer review is on this page. */
  reviewHref: string;
  className?: string;
}

/** Where a gated review has been unlocked: says so, how many analyses are left, and leads into the full review. */
export function InsightsOpenBanner({ analysesRemaining, analysisLimit, reviewHref, className }: InsightsOpenBannerProps) {
  const { t } = useTranslation();
  const { locked } = useExamInsights();
  if (locked) return null;

  return (
    <section aria-label={t('exam_insights.open_label')} className={cn('flex flex-col gap-3 rounded-[20px] bg-[#10182B] p-4 text-white sm:flex-row sm:items-center sm:justify-between sm:p-5', className)}>
      <div className="min-w-0">
        <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#D1FE17]">{t('exam_insights.open_label')}</p>
        {analysisLimit > 0 && (
          <p className="mt-1.5 font-display text-[17px] font-extrabold tracking-[-0.03em]">{t('exam_insights.open_left', { left: analysesRemaining })}</p>
        )}
        <p className="mt-1 text-[12.5px] leading-normal text-[#C9CCE6]">{t('exam_insights.open_note')}</p>
      </div>
      <a
        href={reviewHref}
        className="inline-flex shrink-0 items-center justify-center rounded-xl bg-[#D1FE17] px-4 py-2.5 text-[13px] font-bold text-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        {t('result.view.review_answers')}
      </a>
    </section>
  );
}
